import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { useState } from 'react';
import { MdExpandMore } from 'react-icons/md';
import { toast } from 'react-toastify';

import { queryClient } from '@/App';
import { activityTableColumnsColumns } from '@/constants/tableColumns/ActivityTrainingColumns';
import api from '@/services/apiSgft';
import { userCanUpdateActivity } from '@/utils/handleSavePermissions';

import DeleteIcon from '../../assets/icons/delete-icon.svg';
import EditIcon from '../../assets/icons/edit-icon.svg';
import { ActivityData, EmployeeTypeEnum } from '../../types/ActivityData';
import { Button } from '../atoms/Button';
import { ConfirmationDialog } from '../atoms/ConfirmationDialog';
import Table from '../organisms/Table';
import ActivityModal from './ActivityModal';
import ExpandableSubtitle from './ExpandableSubtitle';

interface ActivityCardProps {
	activity: ActivityData;
}

export const ActivityCard = ({ activity }: ActivityCardProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const [isOpenEdit, setIsOpenEdit] = useState(false);
	const [isOpenDelete, setIsOpenDelete] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const deleteActivity = async () => {
		try {
			setIsDeleting(true);
			await api.delete(`/activity/${activity.id}`);
			toast.success('Atividade removida com sucesso!', {
				theme: 'colored',
				toastId: 'success',
			});
			queryClient.invalidateQueries(['get-all-activities-data']);
		} catch (e) {
			toast.error('Erro ao remover atividade!', {
				theme: 'colored',
				toastId: 'error',
			});
		}
		setIsOpenDelete(false);
		setIsDeleting(false);
	};

	const groupByEmployeeType = (
		activityPoleConditions: ActivityData['activityPoles'][0]['activityPoleConditions'],
	) => {
		const internalConditions = activityPoleConditions.filter(
			(condition) => condition.employeeType === EmployeeTypeEnum.INTERNAL,
		);
		const thirdPartyConditions = activityPoleConditions.filter(
			(condition) => condition.employeeType === EmployeeTypeEnum.THIRD_PARTY,
		);
		return {
			internalConditions,
			thirdPartyConditions,
		};
	};
	const isAllowedToUpdateActivity = userCanUpdateActivity();
	return (
		<>
			{isOpenEdit && (
				<ActivityModal
					isOpen={isOpenEdit}
					onClose={() => setIsOpenEdit(false)}
					type="edit"
					activity={activity}
				/>
			)}
			{isOpenDelete && (
				<ConfirmationDialog
					title={'Confirmação da deleção'}
					description={
						'Ao prosseguir com esta ação, a atividade e todas as suas informações serão removidas do sistema.'
					}
					setIsOpen={setIsOpenDelete}
					onConfirmation={deleteActivity}
					height="15rem"
					width="30rem"
					isLoading={isDeleting}
				/>
			)}
			<AccordionPrimitive.Root
				type="multiple"
				className="w-full rounded-[10px]"
			>
				<AccordionPrimitive.Item
					value="1"
					className="w-full rounded-[10px] bg-white"
				>
					<AccordionPrimitive.Header className="relative w-full rounded-[10px] bg-white">
						<AccordionPrimitive.Trigger
							className="group flex w-full grow rounded-[10px] px-5 py-[18px]"
							asChild
						>
							<div className="flex w-full items-center justify-between">
								<h3 className="text-2xl font-semibold text-primary">
									{activity.name}
								</h3>
								<div className="flex">
									<Button
										disabled={!isAllowedToUpdateActivity}
										aria-label="edit"
										variant={'destructive'}
										className="p-2 hover:opacity-90"
										onClick={(e) => {
											e.stopPropagation();
											setIsOpenEdit(true);
										}}
									>
										<img
											src={EditIcon}
											alt="Ícone de editar"
											className="w-5 flex-none"
										/>
									</Button>

									<Button
										disabled={!isAllowedToUpdateActivity}
										aria-label="delete"
										className="p-2 hover:opacity-90"
										variant={'destructive'}
										onClick={(e) => {
											e.stopPropagation();
											setIsOpenDelete(true);
										}}
									>
										<img
											src={DeleteIcon}
											alt="Ícone de deletar"
											className="w-[13px] flex-none"
										/>
									</Button>
									<Button
										aria-label="delete"
										className="p-2 hover:opacity-90"
										variant={'destructive'}
										onClick={() => setIsOpen(!isOpen)}
									>
										<MdExpandMore className=" text-3xl text-primary transition-transform duration-300 ease-[cubic-bezier(0.87,_0,_0.13,_1)] group-data-[state=open]:rotate-180" />
									</Button>
								</div>
							</div>
						</AccordionPrimitive.Trigger>
					</AccordionPrimitive.Header>
					<AccordionPrimitive.Content className="overflow-hidden rounded-[10px] bg-white px-5 text-[15px] data-[state=closed]:animate-slideUp data-[state=open]:animate-slideDown">
						{activity.activityPoles.map((activityPole) => {
							const { internalConditions, thirdPartyConditions } =
								groupByEmployeeType(activityPole.activityPoleConditions);
							return (
								<section
									className="flex flex-col gap-2 pb-4 pt-[10px]"
									key={activityPole.id}
								>
									<ExpandableSubtitle
										subtitle={`Polo ${activityPole.pole.name}`}
										className="text-lg"
										defaultOpen={false}
									>
										<div className="flex flex-col gap-8">
											<div className="flex flex-col gap-2">
												<h5 className="text-xs font-semibold text-primary">
													Primário
												</h5>

												<Table
													isWhiteTable
													data={internalConditions}
													columns={activityTableColumnsColumns}
												/>
											</div>
											<div className="flex flex-col gap-2">
												<h5 className="text-xs font-semibold text-primary">
													Terceiro
												</h5>

												<Table
													isWhiteTable
													data={thirdPartyConditions}
													columns={activityTableColumnsColumns}
												/>
											</div>
										</div>
									</ExpandableSubtitle>
								</section>
							);
						})}
					</AccordionPrimitive.Content>
				</AccordionPrimitive.Item>
			</AccordionPrimitive.Root>
		</>
	);
};
